///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import {
  contractOnFileTypes,
  mileageBasisTypes,
  tenderLeadTimeTypes,
  bidOutputFormatsTypes,
  fscTypes,
  currencyTypes,
  booleanTypes,
  rateReturnTypes,
  freightClassTypes,
  ltlAcessorialTypes,
  containerSizeTypes,
  imdlSolutionTypes,
  equipmentTypes as equipTypes,
  mode as modeType,
  bidStrategyTypes,
  shipperTenderStrategyTypes,
  shipperOpsOfficeGroup,
  rfqStatusTypes,
  internationalLanesTypes,
  division,
  bidTypes,
  bidScacType,
  numberOfRoundsOptions,
  operationalCenters
} from "./types";
import getStrategyDuration from "./getStrategyDuration";

const closedRfqStatusValue = rfqStatusTypes[5].value;

export const getModeIsReopenAllow = mode =>
  mode.isReopenAllow;

const buildRfqStatusOptions = (mode, isCoyoteBid) => {
  if (!mode.previousStatus) {
    return mode.previousStatus && [
      rfqStatusTypes.find(x => x.value === mode.previousStatus),
      rfqStatusTypes.find(x => x.value === mode.status)
    ];
  }
  const current = rfqStatusTypes.find(x => x.value === mode.status);
  const reopenAllow = getModeIsReopenAllow(mode);
  if (
    !isCoyoteBid &&
    mode.status === closedRfqStatusValue &&
    reopenAllow === false
  ) {
    return [current];
  }
  return [
    rfqStatusTypes.find(x => x.value === mode.previousStatus),
    current
  ];
};

export const reverseMapBidDetails = (bid, bidDivision) => {
  const foShipperCodeValue = bid.isCoyoteBid ? "" : bid.shipperCode;
  
  const mappedBidDetails = {
    bidId: bid.bidId,
    files: [],

    // General
    bidScac: bid.isCoyoteBid ? bidScacType[0] : bidScacType[1],
    rfqName: bid.name,
    shipper: bid.isCoyoteBid
      ? {
          value: bid.shipperId,
          code: bid.shipperCode,
          label: bid.shipperName,
          shipperId: bid.shipperId,
          name: bid.shipperName
        }
      : null,
    mainSalesRepDetails: {
      mainSalesRep: {
        userId: bid.mainRepId,
        fullName: bid.mainRepName
      }
    },
    salesforceOpportunityId: bid.salesforceOpportunityId,
    previousSalesforceOpportunityId: bid.previousSalesforceOpportunityId,
    locationCode: bid.locationCode,
    operationalCenters: bid.operationalCenters
      ? Array.isArray(bid.operationalCenters)
        ? bid.operationalCenters.map(x => operationalCenters.find(y => y.value === x))
        : bid.operationalCenters
      : null,
    bidOutputFormat: bid.outputFormats && bid.outputFormats.length > 0
      ? bid.outputFormats.map(x => bidOutputFormatsTypes.find(y => y.value === x))
      : null,
    contractOnFile:
      bid.contractOnFile !== null
        ? contractOnFileTypes.find(x => x.value === bid.contractOnFile)
        : null,
    mileageBasis: bid.mileageBasis
      ? mileageBasisTypes.find(x => x.value === bid.mileageBasis)
      : null,
    tenderLeadTime: bid.tenderLeadTime
      ? tenderLeadTimeTypes.find(x => x.value === bid.tenderLeadTime)
      : null,
    shipperOpsGroup: bid.shipperOpsGroup ? parseInt(bid.shipperOpsGroup) : "",
    shipperOpsGroupOffice: bid.shipperOpsGroupOffice
      ? shipperOpsOfficeGroup.find(x => x.label === bid.shipperOpsGroupOffice)
      : null,
    totalRounds: bid.isCoyoteBid
      ? bid.totalRounds
      : numberOfRoundsOptions.find(x => x.value === bid.totalRounds),
    bidType: bidTypes.find(x => x.value === bid.bidType),
    foShipperCode: foShipperCodeValue,
    freightOptimizerCodes: !bid.isCoyoteBid
      ? (() => {
          const hasValidFoCodes = bid.freightOptimizerCodes &&
            Array.isArray(bid.freightOptimizerCodes) &&
            bid.freightOptimizerCodes.length > 0;
          if (hasValidFoCodes) return bid.freightOptimizerCodes;

          const hasValidShipperCode = foShipperCodeValue && foShipperCodeValue.trim() !== "";
          return hasValidShipperCode ? [foShipperCodeValue] : null;
        })()
      : null,
    accountOwner: bid.isCoyoteBid ? "" : bid.mainRepName,

    // Financials
    marginExpectationPercentage: parseFloat(bid.marginExpectationPercentage),
    annualSpend: bid.annualSpend ? parseFloat(bid.annualSpend) : "",
    maxCargoValue: bid.maxCargoValue ? parseFloat(bid.maxCargoValue) : "",

    // Timeline
    openDateUTC: bid.openDateUTC,
    closeDateUTC: bid.closeDateUTC,
    additionalBidRounds: bid.additionalBidRounds && Array.isArray(bid.additionalBidRounds)
      ? bid.additionalBidRounds.map(round => ({
          roundNumber: round.roundNumber,
          dueDateUTC: round.dueDateUTC
        }))
      : [],
    startDateUTC: bid.startDateUTC,
    endDateUTC: bid.endDateUTC,
    awardDateUTC: bid.awardDateUTC ? bid.awardDateUTC : null,
    strategicStartDateUTC: bid.strategicStartDateUTC
      ? bid.strategicStartDateUTC
      : null,
    strategicEndDateUTC: bid.strategicEndDateUTC
      ? bid.strategicEndDateUTC
      : null,
    strategyDuration: getStrategyDuration(
      bid.strategicStartDateUTC,
      bid.strategicEndDateUTC
    ),

    //Mode Information & Bid Stratergy
    modes: bid.modes.map(m => {
      if (m.mode === modeType.tl) {
        return mapTlDetails(m, bidDivision, bid.isCoyoteBid);
      } else if (m.mode === modeType.ltl) {
        return mapLtlDetails(m, bid.isCoyoteBid);
      }
      return mapImdlDetails(m, bid.isCoyoteBid);
    })
  };

  return mappedBidDetails;
};

const mapBidModeStrategy = strategy => ({
  bidStrategy: bidStrategyTypes.find(x => x.value === strategy.bidStrategy),
  shipperTenderStrategy: shipperTenderStrategyTypes.find(
    x => x.value === strategy.shipperTenderStrategy
  ),
  expectedLanesPerDay: parseInt(strategy.expectedLanesPerDay),
  goalLanesPerDay: parseInt(strategy.goalLanesPerDay),
  acceptanceParameters: strategy.acceptanceParameters,
  strategyNotes: strategy.notes
});

const mapTlDetails = (mode, bidDivision, isCoyoteBid) => ({
  bidModeId: mode.bidModeId,
  status: rfqStatusTypes.find(x => x.value === mode.status),
  currentStatus: rfqStatusTypes.find(x => x.value === mode.status),
  previousStatus: mode.previousStatus
    ? rfqStatusTypes.find(x => x.value === mode.previousStatus)
    : mode.previousStatus,
  isReopenAllow: getModeIsReopenAllow(mode),
  rfqStatusOptions: buildRfqStatusOptions(mode, isCoyoteBid),
  mode: mode.mode,
  laneCount: parseInt(mode.laneCount),
  volume: parseInt(mode.volume),
  fuelSurchargeType: fscTypes.find(
    x =>
      (!x.division || x.division === bidDivision) &&
      x.value === mode.fuelSurchargeType
  ),
  fuelSurchargePeg:
    mode.fuelSurchargePeg != null && mode.fuelSurchargePeg !== ""
      ? parseFloat(mode.fuelSurchargePeg)
      : "",
  equipmentTypes: [
    ...mode.equipmentTypes.map(x =>
      equipTypes.filter(x => x.type !== 3).find(y => y.type === x)
    ),
    ...mode.equipmentLengths.map(x => equipTypes.find(y => y.length === x))
  ].filter(x => x !== undefined),
  currencyType: currencyTypes.find(x => x.value === mode.currencyType),
  notes: mode.notes,
  internationalLanes:
    !mode.internationalLanes || !mode.internationalLanes.length
      ? null
      : mode.internationalLanes.map(i => ({
          ...internationalLanesTypes.find(t => t.value === i),
          disabled: true
        })),
  additionalCurrencies: mode.additionalCurrencies?.length
    ? mode.additionalCurrencies?.map(x =>
        currencyTypes.find(y => y.value === x)
      )
    : null,
  strategicQuantile: mode.strategy.strategicQuantile,
  rxoFuel: mode.rxoFuel ? mode.rxoFuel : "",
  customerFuel: mode.customerFuel ? mode.customerFuel : "",
  customerBranchAndNetworkTimeframeStartDate:
    mode.customerBranchAndNetworkTimeframeStartDate,
  customerBranchAndNetworkTimeframeEndDate:
    mode.customerBranchAndNetworkTimeframeEndDate,
  bidSubmissionType: mode.bidSubmissionType ?? 0,
  bidSubmissionDetails: mode.bidSubmissionDetails ?? "",
  bidLoginDetails: mode.bidLoginDetails ?? "",
  ...mapBidModeStrategy(mode.strategy)
});

const mapLtlDetails = (mode, isCoyoteBid) => ({
  bidModeId: mode.bidModeId,
  status: rfqStatusTypes.find(x => x.value === mode.status),
  currentStatus: rfqStatusTypes.find(x => x.value === mode.status),
  previousStatus: mode.previousStatus
    ? rfqStatusTypes.find(x => x.value === mode.previousStatus)
    : mode.previousStatus,
  isReopenAllow: getModeIsReopenAllow(mode),
  rfqStatusOptions: buildRfqStatusOptions(mode, isCoyoteBid),
  mode: mode.mode,
  laneCount: parseInt(mode.laneCount),
  volume: parseInt(mode.volume),
  ltlFreightClasses: mode.ltlFreightClasses.map(x =>
    freightClassTypes.find(y => y.value === x)
  ),
  palletsStackable: booleanTypes.find(x => x.value === mode.palletsStackable),
  ltlIncumbentCarriers: mode.ltlIncumbentCarriers,
  ltlPreferredCarriers: mode.ltlPreferredCarriers,
  ltlRateReturnFormat: rateReturnTypes.find(
    x => x.value === mode.ltlRateReturnFormat
  ),
  requiresGuaranteedShipment: booleanTypes.find(
    x => x.value === mode.requiresGuaranteedShipment
  ),
  ltlAccessorials: mode.ltlAccessorials.map(x =>
    ltlAcessorialTypes.find(y => y.value === x)
  ),
  notes: mode.notes,
  additionalCurrencies: mode.additionalCurrencies?.length
    ? mode.additionalCurrencies?.map(x =>
        currencyTypes.find(y => y.value === x)
      )
    : null,
  ...mapBidModeStrategy(mode.strategy)
});

const mapImdlDetails = (mode, isCoyoteBid) => ({
  bidModeId: mode.bidModeId,
  status: rfqStatusTypes.find(x => x.value === mode.status),
  currentStatus: rfqStatusTypes.find(x => x.value === mode.status),
  previousStatus: mode.previousStatus
    ? rfqStatusTypes.find(x => x.value === mode.previousStatus)
    : mode.previousStatus,
  isReopenAllow: getModeIsReopenAllow(mode),
  rfqStatusOptions: buildRfqStatusOptions(mode, isCoyoteBid),
  mode: mode.mode,
  laneCount: parseInt(mode.laneCount),
  volume: parseInt(mode.volume),
  fuelSurchargeType: fscTypes.find(
    x =>
      (!x.division || x.division === division.coy) &&
      x.value === mode.fuelSurchargeType
  ),
  fuelSurchargePeg:
    mode.fuelSurchargePeg != null && mode.fuelSurchargePeg !== ""
      ? parseFloat(mode.fuelSurchargePeg)
      : "",
  equipmentLengths: mode.equipmentLengths?.map(x =>
    isCoyoteBid
      ? containerSizeTypes.find(y => y.value === x) ||
        imdlSolutionTypes.find(y => y.value === x)
      : imdlSolutionTypes.find(y => y.value === x) ||
        containerSizeTypes.find(y => y.value === x)
  ) ?? null,
  palletCount: mode.palletCount ? parseInt(mode.palletCount) : "",
  competitors: mode.competitors ? mode.competitors : "",
  notes: mode.notes ? mode.notes : "",
  additionalCurrencies: mode.additionalCurrencies?.length
    ? mode.additionalCurrencies?.map(x =>
        currencyTypes.find(y => y.value === x)
      )
    : null,
  currencyType: mode.currencyType != null
    ? currencyTypes.find(x => x.value === mode.currencyType) ?? null
    : null,
  bidSubmissionType: mode.bidSubmissionType ?? 0,
  bidLoginDetails: mode.bidLoginDetails ?? "",
  rxoFuel: mode.rxoFuel ? mode.rxoFuel : "",
  customerFuel: mode.customerFuel ? mode.customerFuel : "",
  ...mapBidModeStrategy(mode.strategy)
});

export default reverseMapBidDetails;
